import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Select, RichTextEditor, FileAttachment } from '@components/ui';
import { TASK_PRIORITY, TASK_TYPE, STORY_POINTS } from '@config/constants';
import AIAssistant from './AIAssistant';
import TaskRelationSelector from './TaskRelationSelector';
import CommentSection from './CommentSection';
import ActivityFeed from './ActivityFeed';
import { useTaskRelations, useTaskAttachments, useAddTaskAttachments, useDeleteTaskAttachment } from '@hooks';
import { useAuthStore, useUIStore } from '@store';

const priorityOptions = Object.entries(TASK_PRIORITY).map(([key, value]) => ({
  value: value,
  label: key.charAt(0) + key.slice(1).toLowerCase(),
}));

const typeOptions = Object.entries(TASK_TYPE).map(([key, value]) => ({
  value: value,
  label: key.charAt(0) + key.slice(1).toLowerCase(),
}));

const pointsOptions = STORY_POINTS.map((p) => ({
  value: p,
  label: `${p} puntos`,
}));

export default function TaskModal({
  isOpen,
  onClose,
  onSubmit,
  task = null,
  projectId,
  sprints = [],
  epics = [],
  users = [],
  isLoading = false,
  onOpenFullPage,
  onDelete,
  isDeleting = false,
}) {
  const currentUser = useAuthStore((state) => state.user);
  const setActiveView = useUIStore((state) => state.setActiveView);
  const setSelectedTaskId = useUIStore((state) => state.setSelectedTaskId);
  const { data: relationsData, refetch: refetchRelations } = useTaskRelations(
    task?.id,
    { enabled: !!task?.id }
  );
  const { data: attachmentsData, refetch: refetchAttachments } = useTaskAttachments(
    task?.id,
    { enabled: !!task?.id && isOpen }
  );
  const addAttachments = useAddTaskAttachments();
  const deleteAttachment = useDeleteTaskAttachment();

  const [newFiles, setNewFiles] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleOpenFullPage = () => {
    if (task && onOpenFullPage) {
      onOpenFullPage(task.id);
    } else if (task) {
      setSelectedTaskId(task.id);
      setActiveView('task-detail');
      onClose();
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    type: 'task',
    points: '',
    hours: '',
    sprintId: '',
    epicId: '',
    assigneeIds: [],
    dueDate: '',
    slaDeadline: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority?.toLowerCase() || 'medium',
        type: task.type?.toLowerCase() || 'task',
        points: task.points || '',
        hours: task.hours || '',
        sprintId: task.sprintId || '',
        epicId: task.epicId || '',
        assigneeIds: task.assignees?.map((a) => a.userId || a.user?.id) || [],
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
        slaDeadline: task.slaDeadline ? task.slaDeadline.split('T')[0] : '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        type: 'task',
        points: '',
        hours: '',
        sprintId: '',
        epicId: '',
        assigneeIds: [],
        dueDate: '',
        slaDeadline: '',
      });
    }
    setNewFiles([]);
    setShowDeleteConfirm(false);
  }, [task, isOpen]);

  // Refetch attachments when modal opens with a task
  useEffect(() => {
    if (isOpen && task?.id) {
      refetchAttachments();
    }
  }, [isOpen, task?.id, refetchAttachments]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({ ...prev, description: content }));
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (window.confirm('¿Eliminar este archivo adjunto?')) {
      try {
        await deleteAttachment.mutateAsync(attachmentId);
        refetchAttachments();
      } catch (error) {
        console.error('Error deleting attachment:', error);
      }
    }
  };

  const handleApplyAISuggestions = (suggestions) => {
    setFormData((prev) => ({
      ...prev,
      ...suggestions,
      type: suggestions.type?.toLowerCase() || prev.type,
      priority: suggestions.priority?.toLowerCase() || prev.priority,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await onSubmit({
      ...formData,
      projectId,
      points: formData.points ? parseInt(formData.points) : null,
      hours: formData.hours ? parseFloat(formData.hours) : null,
      sprintId: formData.sprintId ? parseInt(formData.sprintId) : null,
      epicId: formData.epicId ? parseInt(formData.epicId) : null,
      dueDate: formData.dueDate || null,
      slaDeadline: formData.slaDeadline || null,
    });

    // Upload new attachments if task was created/updated successfully
    const taskId = result?.data?.id || task?.id;
    if (taskId && newFiles.length > 0) {
      try {
        await addAttachments.mutateAsync({ taskId, files: newFiles });
        setNewFiles([]);
      } catch (error) {
        console.error('Error uploading attachments:', error);
      }
    }
  };

  const sprintOptions = sprints.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const epicOptions = epics.map((e) => ({
    value: e.id,
    label: e.name,
  }));

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={task ? 'Editar Tarea' : 'Nueva Tarea'}
      size={task ? 'xl' : 'lg'}
      footer={
        <>
          {task && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: 'auto' }}>
              <Button variant="ghost" onClick={handleOpenFullPage}>
                Abrir en página completa
              </Button>
              {!showDeleteConfirm ? (
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ color: '#ef4444' }}
                >
                  Eliminar
                </Button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: '500' }}>
                    ¿Eliminar tarea?
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteConfirm(false)}
                    size="sm"
                  >
                    No
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => onDelete?.(task.id)}
                    loading={isDeleting}
                    size="sm"
                  >
                    Sí, eliminar
                  </Button>
                </div>
              )}
            </div>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isLoading}>
            {task ? 'Guardar' : 'Crear Tarea'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div style={task ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' } : {}}>
        {/* Left column - Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Title with AI button */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Título</label>
              {!task && (
                <AIAssistant
                  title={formData.title}
                  onApplySuggestions={handleApplyAISuggestions}
                />
              )}
            </div>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Título de la tarea"
              required
            />
          </div>

          <div>
            <label style={labelStyle}>Descripción</label>
            <RichTextEditor
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Describe la tarea..."
              minHeight={100}
              attachments={attachmentsData?.data || []}
            />
          </div>

          <div>
            <label style={labelStyle}>Archivos Adjuntos</label>
            <FileAttachment
              files={newFiles}
              existingAttachments={attachmentsData?.data || []}
              onChange={setNewFiles}
              onRemoveExisting={task ? handleDeleteExistingAttachment : undefined}
            />
          </div>

          <div style={gridStyle}>
            <Select
              label="Tipo"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={typeOptions}
            />

            <Select
              label="Prioridad"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              options={priorityOptions}
            />
          </div>

          <div style={gridStyle}>
            <Select
              label="Story Points"
              name="points"
              value={formData.points}
              onChange={handleChange}
              options={pointsOptions}
              placeholder="Seleccionar..."
            />

            <Input
              label="Horas estimadas"
              name="hours"
              type="number"
              min="0"
              step="0.5"
              value={formData.hours}
              onChange={handleChange}
              placeholder="0"
            />
          </div>

          <div style={gridStyle}>
            <Select
              label="Sprint"
              name="sprintId"
              value={formData.sprintId}
              onChange={handleChange}
              options={sprintOptions}
              placeholder="Sin sprint (Backlog)"
            />

            <Select
              label="Epic"
              name="epicId"
              value={formData.epicId}
              onChange={handleChange}
              options={epicOptions}
              placeholder="Sin epic"
            />
          </div>

          <div style={gridStyle}>
            <Input
              label="Fecha Límite"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={handleChange}
            />

            <Input
              label="Fecha SLA"
              name="slaDeadline"
              type="date"
              value={formData.slaDeadline}
              onChange={handleChange}
            />
          </div>

          {/* User Assignment */}
          <div>
            <label style={labelStyle}>Asignados</label>
              {users.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    minHeight: '44px',
                  }}
                >
                  {users.map((user) => {
                    const isSelected = formData.assigneeIds.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            assigneeIds: isSelected
                              ? prev.assigneeIds.filter((id) => id !== user.id)
                              : [...prev.assigneeIds, user.id],
                          }));
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          border: isSelected ? '2px solid #667eea' : '1px solid #e2e8f0',
                          backgroundColor: isSelected ? '#667eea15' : 'white',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: isSelected ? '600' : '400',
                          color: isSelected ? '#667eea' : '#475569',
                          transition: 'all 0.2s',
                        }}
                      >
                        <span
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: user.avatar
                              ? `url(${user.avatar}) center/cover`
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '600',
                          }}
                        >
                          {!user.avatar && user.name?.charAt(0).toUpperCase()}
                        </span>
                        {user.name}
                        {isSelected && <span style={{ marginLeft: '4px' }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize: '13px', color: '#94a3b8', padding: '10px' }}>
                  No hay miembros en el proyecto
                </p>
              )}
          </div>

        </div>

        {/* Right column - Activity, Relations & Comments (only when editing) */}
        {task && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', borderLeft: '1px solid #e2e8f0', paddingLeft: '24px' }}>
            {/* Task Relations */}
            <TaskRelationSelector
              taskId={task.id}
              projectId={projectId}
              currentRelations={relationsData?.data || { blocking: [], blockedBy: [], relatedTo: [] }}
              onRelationChange={refetchRelations}
            />

            {/* Activity History */}
            <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px' }}>
              <ActivityFeed entity="task" entityId={task.id} limit={10} />
            </div>

            {/* Comments Section */}
            <CommentSection
              taskId={task.id}
              currentUserId={currentUser?.id}
            />
          </div>
        )}
        </div>
      </form>
    </Modal>
  );
}

TaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  task: PropTypes.object,
  projectId: PropTypes.number,
  sprints: PropTypes.array,
  epics: PropTypes.array,
  users: PropTypes.array,
  isLoading: PropTypes.bool,
  onOpenFullPage: PropTypes.func,
  onDelete: PropTypes.func,
  isDeleting: PropTypes.bool,
};
